<template>
  <div id="pos" class="container">

    <div class="step">
      <div class="title"><span class="number">1.</span> {{ $t("pos.description_1") }}</div>
      <div class="description">{{ $t("pos.description_2") }}</div>
      <div class="cols">
        <div class="col">
          <form @submit.prevent="submitAccountNumber">
            <label for="txtAccountNumber">{{ $t("pos.account_barcode") }}</label>
            <input id="txtAccountNumber" type="text" ref="txtAccountNumber" v-model="accountNumber"
              :disabled="accountData">
          </form>
          <button class="btn btn-primary" v-if="accountData" style="margin-top: 10px;" @click="reset">{{
            $t("pos.change_card")
          }}</button>
        </div>
        <div class="col" :class="{ invisible: !customer }">
          <label>{{ $t("pos.client_info") }}</label>
          <div class="info" v-if="customer">
            <template v-if="customer.user.firstName || customer.user.lastName">
              {{ customer.user.firstName + " " + customer.user.lastName }}<br>
            </template>
            <template v-if="customer.information && customer.information.birthDate">
              {{ moment(customer.information.birthDate).format("DD MMM YYYY") }}
            </template>
            <template v-if="customer.information && customer.information.contact.email">
              {{ customer.information.contact.email }}<br>
            </template>
            <template v-if="customer.information && customer.information.contact.phoneNumber">
              {{ formatPhoneNumber(customer.information.contact.phoneNumber) }}
            </template>
            <div class="image" v-if="customer.information && customer.information.image">
              <img :src="customerImageLink(customer)">
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="step" v-if="accountData">
      <div class="title"><span class="number">2.</span> {{ $t("pos.title_2") }}</div>
      <div class="cols">
        <div class="col">
          <label>{{ $t("pos.amount_to_debit") }}</label>
          <input type="number" v-model="amount">
        </div>
        <div class="col">
          <label>{{ $t("pos.balance") }}</label>
          <div class="info big">{{ $tc(accountData.balance, accountData.currency) }}</div>
        </div>
      </div>
    </div>

    <div class="calculator">
      <div class="keys">
        <div class="key" @click="key(7)">7</div>
        <div class="key" @click="key(8)">8</div>
        <div class="key" @click="key(9)">9</div>
        <div class="key" @click="keyBack">BACK</div>
        <div class="key" @click="key(4)">4</div>
        <div class="key" @click="key(5)">5</div>
        <div class="key" @click="key(6)">6</div>
        <div class="key" @click="keyClear">C</div>
        <div class="key" @click="key(1)">1</div>
        <div class="key" @click="key(2)">2</div>
        <div class="key" @click="key(3)">3</div>
        <div class="key h2" @click="keyEnter">{{ $t("pos.enter") }}</div>
        <div class="key w2" @click="key(0)">0</div>
        <div class="key">00</div>
      </div>
      <div class="btn btn-primary" :class="{ disabled: !amount }" @click="confirm">{{ $t("pos.confirm") }}</div>
    </div>

  </div>
</template>

<script>

  import { Core } from "../../core/Core";
  import moment from "moment";

  export default {

    name: "POS",

    data() {
      return {
        accountNumber: "",
        accountData: null,
        customer: null,
        amount: 0,
        moment
      };
    },

    mounted() {
      this.$store.state.pageTitleTranslationKey = "pos.title";
      this.$store.state.pageIcon = "/icon/wallet.svg";
      this.$refs.txtAccountNumber.focus();
    },

    watch: {
      amount(newValue) {
        if (this.accountData && newValue > this.accountData.balance) {
          this.amount = this.accountData.balance;
        }
      }
    },

    computed: {
      companyData() {
        let contextCompany = this.$store.state.contextCompany;
        if (contextCompany) {
          return {
            id: contextCompany.id,
            name: contextCompany.name,
            canonical: contextCompany.nameCanonical
          }
        } else {
          return null;
        }
      },
      formatPhoneNumber() {
        return n => {
          return "(" + n.slice(0, 3) + ") " + n.slice(3, 6) + "-" + n.slice(6, 11);
        }
      }
    },

    methods: {
      customerImageLink(customer) {
        if (customer.information && customer.information.image) {
          return Parameters.urlFileServer + customer.information.image
        } else {
          return ''
        }
      },
      reset() {
        this.accountNumber = "";
        this.accountData = null;
        this.customer = null;
        this.amount = 0;
        this.$refs.txtAccountNumber.focus();
      },
      async submitAccountNumber() {
        let response = await Core.request("GET", "/api/loyalty_accounts", {
          cardNumber: this.accountNumber,
          augment: 'customers'
        });
        let loyaltyResponse = response.data[0] || null;
        if (!loyaltyResponse) {
          toast(this.$t("pos.no_account_found"), "error");
          return;
        }

        let customers = loyaltyResponse.customers
          .filter(c => c.permissions.includes('consume'))
          .sort((a, b) => a.permissions.length - b.permissions.length)
          .map(c => c.customer);

        if (!customers.length) {
          toast(this.$t("pos.no_account_found"), "error");
          return;
        }
        this.accountData = loyaltyResponse;
        if (customers.length === 1) {
          this.customer = customers[0] || null;
        } else if (customers.length > 1) {
          this.customer = customers[0] || null; //sort by smaller permission
        }
      },
      async confirm() {
        let result = await Core.request("POST", `/api/loyalty_accounts/${this.accountData.id}/sale`, {
          points: 0,
          money: -this.amount,
          moreData: {
            note: "POS",
            company: this.companyData,
          }
        });
        if (result) {
          this.reset();
          toast(this.$t("pos.success"), "success");
        } else {
          toast(this.$t("pos.error"), "error");
        }
      },
      key(number) {
        if (!this.accountData) {
          this.accountNumber = parseInt(this.accountNumber || 0) * 10 + number;
        } else {
          this.amount = parseInt(this.amount || 0) * 10 + number;
        }
      },
      keyClear() {
        if (!this.accountData) {
          this.accountNumber = "";
        } else {
          this.amount = "";
        }
      },
      keyBack() {
        if (!this.accountData) {
          this.accountNumber = Math.floor(parseInt(this.accountNumber || 0) / 10);
        } else {
          this.amount = Math.floor(parseInt(this.amount || 0) / 10);
        }
      },
      keyEnter() {
        if (!this.accountData) {
          this.submitAccountNumber();
        } else if (this.amount) {
          this.confirm();
        }
      }
    }

  }

</script>

<style lang="scss" scoped>
  .container {
    display: flex;
    flex-direction: column;
  }

  .step {
    background-color: white;
    border-radius: 2px;
    padding: 20px 30px 25px;
    margin-bottom: 20px;
  }

  .title {
    font-size: 18px;
    text-align: center;
    border-bottom: 1px solid #ddd;
    padding-bottom: 6px;
    margin-bottom: 8px;

    .number {
      font-weight: bold;
      color: var(--primary-color);
    }
  }

  .cols {
    margin-top: 20px;
    display: flex;

    .col {
      flex-grow: 1;
      flex-basis: 0;

      &:not(:last-child) {
        margin-right: 40px;
      }

      input,
      .btn {
        width: 100%;
      }
    }
  }

  label {
    margin-bottom: 4px;
    padding-left: 15px;
  }

  .info {
    background-color: #eee;
    padding: 10px 15px;
    border-radius: 4px;

    &.big {
      font-size: 18px;
    }
  }

  .calculator {
    margin-top: 20px;
    align-self: center;

    .btn {
      text-align: center;
      padding: 15px 20px;
    }
  }

  .keys {
    display: grid;
    grid-template-columns: repeat(3, 80px) 120px;
    grid-template-rows: repeat(4, 55px);
    grid-gap: 10px;
    padding-bottom: 15px;
    margin-bottom: 15px;
    border-bottom: 1px solid #ddd;

    .key {
      background-color: #ddd;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #666;

      &.h2 {
        grid-row-end: span 2;
      }

      &.w2 {
        grid-column-end: span 2;
      }
    }
  }

  .image {
    background-color: white;
    padding: 10px;
    margin-top: 10px;

    img {
      max-width: 200px;
      max-height: 355px;
    }
  }

  .invisible {
    opacity: 0;
    pointer-events: none;
  }
</style>